import React, { useState } from 'react';
import { getCloudflareImageUrl } from '../utils/cloudflareStream';

/**
 * CloudflareImage Component
 * Renders an image from Cloudflare Images with different variants and loading states
 */
const CloudflareImage = ({
  imageId,
  variant = 'public',
  alt = 'Image',
  width,
  height,
  className = '',
  style = {},
  loading = 'lazy',
  onLoad,
  onError,
  placeholder = true,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Generate image URL
  const imageUrl = getCloudflareImageUrl(imageId, variant);

  const handleLoad = (e) => {
    setIsLoading(false);
    if (onLoad) onLoad(e);
  };

  const handleError = (e) => {
    setIsLoading(false);
    setHasError(true);
    if (onError) onError(e);
  };

  const imageStyle = {
    display: hasError ? 'none' : 'block',
    maxWidth: '100%',
    height: 'auto',
    opacity: isLoading ? '0' : '1',
    transition: 'opacity 0.3s ease',
    ...style,
  };

  if (width) imageStyle.width = width;
  if (height) imageStyle.height = height;

  return (
    <div className={`cloudflare-image-container ${className}`} style={{ position: 'relative' }}>
      {/* Loading placeholder */}
      {isLoading && placeholder && !hasError && (
        <div
          className="cloudflare-image-placeholder"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: width || '100%',
            height: height || '200px',
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
            fontSize: '14px',
            borderRadius: '4px',
            zIndex: 1,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ddd',
                borderTop: '2px solid #666',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            Loading image...
          </div>
        </div>
      )}

      {/* Error placeholder */}
      {hasError && (
        <div
          className="cloudflare-image-error"
          style={{
            width: width || '100%',
            height: height || '200px',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            fontSize: '12px',
            borderRadius: '4px',
            border: '2px dashed #ddd',
            padding: '1rem',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>📷</div>
          <div>Image not available</div>
          <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '4px' }}>ID: {imageId}</div>
        </div>
      )}

      {/* Actual image */}
      <img
        src={imageUrl}
        alt={alt}
        style={imageStyle}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};

/**
 * CloudflareImageGallery Component
 * Renders a gallery of Cloudflare Images with optional lightbox functionality
 */
export const CloudflareImageGallery = ({
  images,
  variant = 'public',
  thumbnailVariant = 'thumbnail',
  columns = 3,
  gap = '1rem',
  className = '',
  onImageClick,
}) => {
  const galleryStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap,
    width: '100%',
  };

  const handleImageClick = (image, index) => {
    if (onImageClick) {
      onImageClick(image, index);
    }
  };

  return (
    <div className={`cloudflare-gallery ${className}`} style={galleryStyle}>
      {images.map((image, index) => {
        const imageId = typeof image === 'string' ? image : image.id;
        const imageAlt = typeof image === 'object' ? image.alt : `Gallery image ${index + 1}`;
        
        return (
          <div
            key={imageId}
            className="gallery-item"
            onClick={() => handleImageClick(image, index)}
            style={{
              cursor: onImageClick ? 'pointer' : 'default',
            }}
          >
            <CloudflareImage
              imageId={imageId}
              variant={thumbnailVariant}
              alt={imageAlt}
              style={{
                width: '100%',
                height: '200px',
                objectFit: 'cover',
                borderRadius: '8px',
              }}
            />
          </div>
        );
      })}
    </div>
  );
};

/**
 * ResponsiveCloudflareImage Component
 * Responsive image that maintains aspect ratio
 */
export const ResponsiveCloudflareImage = ({
  imageId,
  variant = 'public',
  aspectRatio = '16:9',
  maxWidth = '100%',
  ...otherProps
}) => {
  const [width, height] = aspectRatio.split(':').map(Number);
  const paddingTop = `${(height / width) * 100}%`;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth,
        paddingTop,
      }}
    >
      <CloudflareImage
        imageId={imageId}
        variant={variant}
        {...otherProps}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          ...otherProps.style,
        }}
      />
    </div>
  );
};

export default CloudflareImage;